/**
 * Inputs: a booking on the shared trip. Outputs: owner edits to amount, fares,
 * household shares and payment terms. Does not record a supplier payment.
 */

import { useMemo, useState } from "react";
import { bookingHasFinancialHistory, currencyChangeBlocked } from "@shared/financial.ts";
import { formatMinor } from "@shared/money.ts";
import {
  buildPriceBasis,
  majorInputFromMinor,
  parseMajorInput,
  priceBasisSummary,
  priceBasisTotalMinor,
} from "@shared/price-basis.ts";
import type { Booking, TripDocument } from "@shared/schema.ts";
import type { SaveResult } from "../../app/trip.tsx";

export function BookingEditForm({
  trip,
  booking,
  saving,
  onSave,
}: {
  trip: TripDocument;
  booking: Booking;
  saving: boolean;
  onSave: (next: TripDocument) => Promise<SaveResult>;
}) {
  const hasHistory = bookingHasFinancialHistory(trip, booking.id);
  const showFares = booking.kind === "excursion" || Boolean(booking.priceBasis);
  const showDates = booking.kind === "hotel_reservation";
  const [startDate, setStartDate] = useState(booking.startDate ?? "");
  const [endDate, setEndDate] = useState(booking.endDate ?? "");
  const [amount, setAmount] = useState(booking.cost ? majorInputFromMinor(booking.cost.amountMinor, booking.cost.currency) : "");
  const [currency, setCurrency] = useState(booking.cost?.currency ?? "THB");
  const [shares, setShares] = useState<Record<string, string>>(() => {
    const next: Record<string, string> = {};
    for (const household of trip.households) {
      next[household.id] = String((booking.allocations.find((a) => a.householdId === household.id)?.amountMinor ?? 0) / 100);
    }
    return next;
  });
  const [confirmation, setConfirmation] = useState(booking.confirmation ?? "");
  const [termsKind, setTermsKind] = useState(booking.paymentTerms?.kind ?? "unknown");
  const [termsDate, setTermsDate] = useState(booking.paymentTerms?.date ?? "");
  const [contactId, setContactId] = useState(booking.contactId ?? "");
  const [adultCount, setAdultCount] = useState(String(booking.priceBasis?.adultCount ?? ""));
  const [adultAmount, setAdultAmount] = useState(
    booking.priceBasis ? majorInputFromMinor(booking.priceBasis.adultAmountMinor, booking.cost?.currency ?? currency) : "",
  );
  const [paidChildCount, setPaidChildCount] = useState(String(booking.priceBasis?.paidChildCount ?? ""));
  const [childAmount, setChildAmount] = useState(
    booking.priceBasis ? majorInputFromMinor(booking.priceBasis.childAmountMinor, booking.cost?.currency ?? currency) : "",
  );
  const [freeChildCount, setFreeChildCount] = useState(String(booking.priceBasis?.freeChildCount ?? ""));
  const [ageException, setAgeException] = useState(booking.priceBasis?.ageException ?? "");
  const [allocationStatus, setAllocationStatus] = useState(booking.allocationStatus ?? "proposed_not_explicitly_confirmed");
  const [message, setMessage] = useState<string | null>(null);

  const farePreview = useMemo(() => {
    if (!showFares) return null;
    const built = buildPriceBasis({
      adultCount,
      adultAmount,
      paidChildCount,
      childAmount,
      freeChildCount,
      ageException,
      currency,
    });
    if (built.error) return { error: built.error, total: null as number | null, summary: null as string | null };
    return {
      error: null as string | null,
      total: priceBasisTotalMinor(built.basis),
      summary: priceBasisSummary(built.basis, currency),
      basis: built.basis,
    };
  }, [showFares, adultCount, adultAmount, paidChildCount, childAmount, freeChildCount, ageException, currency]);

  return (
    <form
      className="card"
      onSubmit={(e) => {
        e.preventDefault();
        const blocked = currencyChangeBlocked(booking, currency, hasHistory);
        if (blocked) {
          setMessage(blocked);
          return;
        }
        let amountMinor = amount ? parseMajorInput(amount, currency) : 0;
        if (amountMinor == null) {
          setMessage("Enter a valid amount.");
          return;
        }
        let nextBasis = booking.priceBasis;
        if (showFares) {
          const built = buildPriceBasis({
            adultCount,
            adultAmount,
            paidChildCount,
            childAmount,
            freeChildCount,
            ageException,
            currency,
          });
          if (built.error) {
            setMessage(built.error);
            return;
          }
          nextBasis = built.basis;
          amountMinor = priceBasisTotalMinor(built.basis);
        }
        const allocations = trip.households.map((household) => {
          const raw = shares[household.id] ?? "0";
          const amountForShare = Math.round(Number(raw) * 100);
          return amountForShare > 0 ? { householdId: household.id, amountMinor: amountForShare } : null;
        }).filter((row): row is { householdId: string; amountMinor: number } => Boolean(row));
        if (amountMinor > 0 && allocations.length && allocations.reduce((sum, row) => sum + row.amountMinor, 0) !== amountMinor) {
          setMessage("Household shares must add up to the group total.");
          return;
        }
        if (startDate && endDate && endDate < startDate) {
          setMessage("The end date cannot be before the start date.");
          return;
        }
        void onSave({
          ...trip,
          bookings: trip.bookings.map((row) => row.id === booking.id ? {
            ...row,
            startDate: showDates ? (startDate || null) : row.startDate,
            endDate: showDates ? (endDate || null) : row.endDate,
            confirmation: confirmation.trim() || null,
            contactId: contactId || null,
            cost: amountMinor > 0 ? { currency, amountMinor } : row.cost,
            allocations: allocations.length ? allocations : row.allocations,
            allocationStatus,
            priceBasis: nextBasis,
            paymentTerms: {
              ...row.paymentTerms,
              kind: termsKind as NonNullable<Booking["paymentTerms"]>["kind"],
              date: termsDate || null,
            },
          } : row),
        }).then((result) => setMessage(result.ok ? "Saved." : result.message)).catch((err: Error) => setMessage(err.message));
      }}
    >
      <h3 className="section-title">Edit amounts</h3>
      <p className="muted">This changes the booking total and household shares. It does not record that anyone has paid.</p>
      {showDates ? (
        <>
          <label className="field">Start date<input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
          <label className="field">End date<input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></label>
        </>
      ) : null}
      {showFares ? (
        <>
          <h4 className="serif" style={{ fontSize: "1.05rem", marginBottom: 0 }}>People and fares</h4>
          <label className="field">Adults
            <input value={adultCount} onChange={(e) => setAdultCount(e.target.value)} inputMode="numeric" />
          </label>
          <label className="field">Adult fare
            <input value={adultAmount} onChange={(e) => setAdultAmount(e.target.value)} inputMode="decimal" />
          </label>
          <label className="field">Paid children
            <input value={paidChildCount} onChange={(e) => setPaidChildCount(e.target.value)} inputMode="numeric" />
          </label>
          <label className="field">Child fare
            <input value={childAmount} onChange={(e) => setChildAmount(e.target.value)} inputMode="decimal" />
          </label>
          <label className="field">Free children
            <input value={freeChildCount} onChange={(e) => setFreeChildCount(e.target.value)} inputMode="numeric" />
          </label>
          <label className="field">Age note
            <input value={ageException} onChange={(e) => setAgeException(e.target.value)} placeholder="For example an 11-year-old on the child fare" />
          </label>
          {farePreview?.error ? <p className="banner crit">{farePreview.error}</p> : null}
          {farePreview?.total != null ? (
            <p>
              Group total {formatMinor(currency, farePreview.total)}
              {farePreview.summary ? <span className="muted"> · {farePreview.summary}</span> : null}
            </p>
          ) : null}
        </>
      ) : (
        <label className="field">Amount<input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" /></label>
      )}
      <label className="field">Currency
        <select value={currency} onChange={(e) => setCurrency(e.target.value)} disabled={hasHistory}>
          <option value="GBP">GBP</option>
          <option value="THB">THB</option>
        </select>
      </label>
      {hasHistory ? <p className="muted">Currency is locked after a payment or refund is recorded.</p> : null}
      {trip.households.map((household) => (
        <label className="field" key={household.id}>
          {household.label} share
          <input
            value={shares[household.id] ?? ""}
            onChange={(e) => setShares((current) => ({ ...current, [household.id]: e.target.value }))}
            inputMode="decimal"
          />
        </label>
      ))}
      <label className="field">Household split
        <select value={allocationStatus} onChange={(e) => setAllocationStatus(e.target.value)}>
          <option value="proposed_not_explicitly_confirmed">Proposed, not yet confirmed</option>
          <option value="owner_confirmed_split">Confirmed by us</option>
          <option value="confirmed_50_50">Confirmed 50/50</option>
          <option value="split_to_confirm">Split to confirm</option>
          <option value="assigned">Assigned to one household</option>
        </select>
      </label>
      <label className="field">Confirmation<input value={confirmation} onChange={(e) => setConfirmation(e.target.value)} /></label>
      <label className="field">Payment terms
        <select value={termsKind} onChange={(e) => setTermsKind(e.target.value as NonNullable<Booking["paymentTerms"]>["kind"])}>
          <option value="expected_date">Expected date</option>
          <option value="tentative_date">Around this date</option>
          <option value="confirmed_deadline">Deadline</option>
          <option value="pay_at_property">Pay at the property</option>
          <option value="unknown">Unknown</option>
        </select>
      </label>
      <label className="field">Payment date<input type="date" value={termsDate} onChange={(e) => setTermsDate(e.target.value)} /></label>
      <label className="field">Supplier contact
        <select value={contactId} onChange={(e) => setContactId(e.target.value)}>
          <option value="">None linked</option>
          {trip.contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </label>
      {message ? <p role="status">{message}</p> : null}
      <button className="btn" type="submit" disabled={saving}>{saving ? "Saving…" : "Save amounts"}</button>
    </form>
  );
}
